/**
 * Care medication service - coordination only.
 * No dosage advice or medical recommendations. Tracks compliance (taken/missed).
 */
import { Platform } from 'react-native';
import type { CareMedication } from '../types/care';

const COLLECTION = 'care';
const MEDICATIONS_DOC = 'medications';

function getDb(): Promise<any> {
  if (Platform.OS === 'web') {
    return import('./unifiedAuthService').then((m) => m.getWebDb());
  }
  const { initializeFirebase, getMobileFirestore } = require('./unifiedAuthService');
  if (!initializeFirebase()) return Promise.resolve(null);
  const getFs = getMobileFirestore();
  return Promise.resolve(getFs ? getFs() : null);
}

function medsPath(recipientId: string): string {
  return `users/${recipientId}/${COLLECTION}/${MEDICATIONS_DOC}`;
}

export const careMedicationService = {
  async list(recipientId: string): Promise<CareMedication[]> {
    const db = await getDb();
    if (!db) return [];
    try {
      if (Platform.OS === 'web') {
        const { doc, getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(db, medsPath(recipientId)));
        const data = snap.exists() ? snap.data() : null;
        const list = (data?.items ?? []) as CareMedication[];
        return list.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
      }
      const ref = db.collection('users').doc(recipientId).collection(COLLECTION).doc(MEDICATIONS_DOC);
      const snap = await ref.get();
      const data = snap.exists ? snap.data() : null;
      const list = (data?.items ?? []) as CareMedication[];
      return list.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    } catch (e) {
      console.warn('careMedicationService.list error:', e);
      return [];
    }
  },

  /** Returns meds that have at least one reminder time today (for "today's meds" with status). */
  getTodaysMeds(medications: CareMedication[], todayDate: string): CareMedication[] {
    return medications.filter((m) => {
      if (!m.reminderTimes || m.reminderTimes.length === 0) return true;
      return true; // MVP: show all meds in "today's" list; later filter by schedule
    });
  },

  /** Whether a med is considered "taken" today (lastTakenAt is on todayDate). */
  isTakenToday(med: CareMedication, todayDate: string): boolean {
    if (!med.lastTakenAt) return false;
    return med.lastTakenAt.startsWith(todayDate);
  },

  /** Whether a med is "missed" today: has reminder times in the past and not taken. */
  isMissedToday(med: CareMedication, todayDate: string): boolean {
    if (this.isTakenToday(med, todayDate)) return false;
    if (!med.reminderTimes || med.reminderTimes.length === 0) return false;
    const now = new Date();
    const today = now.toISOString().slice(0, 10);
    if (today !== todayDate) return false;
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    for (const t of med.reminderTimes) {
      const [h, m] = t.split(':').map(Number);
      const slotMinutes = h * 60 + m;
      if (slotMinutes < currentMinutes) return true; // at least one slot passed
    }
    return false;
  },

  async add(recipientId: string, med: Omit<CareMedication, 'id' | 'createdAt'>): Promise<CareMedication> {
    const list = await this.list(recipientId);
    const newMed: CareMedication = {
      ...med,
      id: `med_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      createdAt: new Date().toISOString(),
    };
    list.unshift(newMed);
    await this.saveAll(recipientId, list);
    return newMed;
  },

  async update(recipientId: string, medId: string, patch: Partial<CareMedication>): Promise<void> {
    const list = await this.list(recipientId);
    const idx = list.findIndex((m) => m.id === medId);
    if (idx === -1) return;
    list[idx] = { ...list[idx], ...patch };
    await this.saveAll(recipientId, list);
  },

  async markTaken(recipientId: string, medId: string): Promise<void> {
    await this.update(recipientId, medId, { lastTakenAt: new Date().toISOString() });
  },

  async delete(recipientId: string, medId: string): Promise<void> {
    const list = (await this.list(recipientId)).filter((m) => m.id !== medId);
    await this.saveAll(recipientId, list);
  },

  async saveAll(recipientId: string, items: CareMedication[]): Promise<void> {
    const db = await getDb();
    if (!db) return;
    const payload = { items, updatedAt: new Date().toISOString() };
    try {
      if (Platform.OS === 'web') {
        const { doc, setDoc } = await import('firebase/firestore');
        await setDoc(doc(db, medsPath(recipientId)), payload, { merge: true });
      } else {
        await db.collection('users').doc(recipientId).collection(COLLECTION).doc(MEDICATIONS_DOC).set(payload, { merge: true });
      }
    } catch (e) {
      console.warn('careMedicationService.saveAll error:', e);
      throw e;
    }
  },
};
